import { QueryClient } from "@tanstack/react-query";
import { LoaderFunctionArgs } from "react-router-dom";

import { orgStorage } from "@/shared/auth";
import {
  ActionParameter,
  ActionRecord,
  ActionService,
  InvocationRecord,
  isActionRecord,
  isInvocationRecord,
} from "@/shared/domain/actions";
import * as Invocation from "@/shared/domain/actions/hooks/useInvocation";
import { makeAuthenticatedHttpClient } from "@/shared/http/makeAuthenticatedHttpClient";

import { stringifyCommand } from "../components/action/actionUtils";

export interface InvokeActionLoaderData {
  actionDigest: string | undefined;
  actionName: string | undefined;
  actionOwner: string | undefined;
  initialFormFields: InvokeActionForm;
}

interface InvokeActionForm {
  parameters: ActionParameter[] | undefined;
  cpu: string | undefined;
  gpu: string | undefined;
  memory: string | undefined;
  storage: number | undefined;
  workdir: string | undefined;
  command: string | undefined;
  entrypoint: string | undefined;
  envVars: Record<string, string> | undefined;
  actionTimeout: number | undefined;
  parameterValues: { [key: string]: unknown } | undefined;
  inputData: string[] | undefined;
}

/**
 * Loader function for the /actions/invoke route.
 * Determines which records to fetch and loads them into the cache as needed.
 */
export const loader =
  (queryClient: QueryClient) =>
  async ({
    request,
  }: LoaderFunctionArgs): Promise<InvokeActionLoaderData | null> => {
    const url = new URL(request.url);

    const actionName = url.searchParams.get("action_name");
    const actionOwner = url.searchParams.get("action_owner");
    const baseInvocationId = url.searchParams.get("base_invocation");
    const actionDigest = url.searchParams.get("action_digest");
    const inputFiles = url.searchParams.get("input_files") ?? undefined;

    const orgRecord = orgStorage.getCurrentOrg();

    const httpClient = await makeAuthenticatedHttpClient();
    const actionService = new ActionService(httpClient);

    let actionRecord: ActionRecord | undefined = undefined;
    let invocationRecord: InvocationRecord | undefined = undefined;

    try {
      const promises = [];

      if (baseInvocationId !== null && baseInvocationId.length > 0) {
        // Pre-fetch invocation record if applicable
        promises.push(
          queryClient.ensureQueryData({
            queryKey: Invocation.getQueryKey(baseInvocationId),
            queryFn: ({ signal }) => {
              return actionService.getInvocation(baseInvocationId, undefined, {
                abortSignal: signal,
                resourceOwnerId: actionOwner ?? orgRecord?.org_id,
              });
            },
          }),
        );
      }

      if (actionName !== null && actionName.length > 0) {
        // Pre-fetch action record if applicable
        promises.push(
          queryClient.ensureQueryData({
            queryKey: ["actionRecord", actionName],
            queryFn: ({ signal }) => {
              return actionService.getAction(actionName, undefined, {
                abortSignal: signal,
                resourceOwnerId: actionOwner ?? orgRecord?.org_id,
              });
            },
          }),
        );
      }

      const results = await Promise.all(promises);

      for (const result of results) {
        if (isInvocationRecord(result)) {
          invocationRecord = result;
        } else if (isActionRecord(result)) {
          actionRecord = result;
        }
      }
    } catch {
      return null;
    }

    const initialState = computeInitialState(
      actionRecord,
      invocationRecord,
      inputFiles,
    );

    return {
      actionName: actionName ?? undefined,
      actionOwner: actionOwner ?? undefined,
      actionDigest: actionDigest ?? undefined,
      initialFormFields: initialState,
    };
  };

const computeInitialState = (
  action: ActionRecord | undefined,
  invocation: InvocationRecord | undefined,
  inputFiles: string | undefined,
): InvokeActionForm => {
  let parameters = undefined;

  let cpu = undefined;
  let memory = undefined;
  let gpu = undefined;
  let storage = undefined;
  let workdir = undefined;
  let command = undefined;
  let envVars = undefined;
  let actionTimeout = undefined;
  let entrypoint = undefined;

  // invocation only params
  let parameterValues = undefined;
  let inputData = undefined;

  if (action) {
    const computeReqs = action.compute_requirements;
    const containerParams = action.container_parameters;

    parameters = action.parameters;
    cpu = computeReqs?.vCPU.toString();
    memory = computeReqs?.memory.toString();
    gpu = computeReqs?.gpu.toString();
    storage = computeReqs?.storage;
    workdir = containerParams?.workdir ?? undefined;
    command = stringifyCommand(containerParams?.command ?? null);
    envVars = containerParams?.env_vars ?? {};
    actionTimeout = action.timeout;
    if (
      containerParams?.entry_point &&
      containerParams?.entry_point.length > 0
    ) {
      entrypoint = containerParams?.entry_point[0];
    }
  }

  // base_invocation_id (action cloning) should take precedence, so set these params last
  if (invocation) {
    cpu = invocation.compute_requirements.vCPU?.toString();
    memory = invocation.compute_requirements.memory?.toString();
    gpu = invocation.compute_requirements.gpu?.toString();
    storage = invocation.compute_requirements.storage;
    workdir = invocation.container_parameters.workdir ?? undefined;
    command = stringifyCommand(invocation.container_parameters.command);
    if (invocation.container_parameters.entry_point) {
      entrypoint = invocation.container_parameters.entry_point[0];
    }
    parameterValues = invocation.parameter_values;
    inputData = invocation.input_data;
    envVars = invocation.container_parameters.env_vars ?? {};
    actionTimeout = invocation.timeout ?? undefined;
  }

  if (inputFiles) {
    inputData = inputFiles.split(",");
  }

  return {
    parameters,
    cpu,
    memory,
    gpu,
    storage,
    workdir,
    command,
    entrypoint,
    envVars,
    actionTimeout,
    parameterValues,
    inputData,
  };
};
